'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import {
  ArrowLeft,
  Package,
  Truck,
  CheckCircle,
  Clock,
  MapPin,
} from 'lucide-react';

interface OrderLineItem {
  id: string;
  productTitle: string;
  variantTitle?: string;
  quantity: number;
  unitPrice: number;
  imageUrl?: string;
}

interface OrderDetailData {
  id: string;
  orderNumber: string;
  status: string;
  subtotal: number;
  taxTotal: number;
  shippingTotal: number;
  discountTotal: number;
  total: number;
  currency: string;
  createdAt: string;
  lineItems: OrderLineItem[];
  shippingAddress?: {
    line1: string;
    line2?: string;
    city: string;
    state?: string;
    postalCode: string;
    country: string;
  };
  trackingNumber?: string;
  trackingUrl?: string;
}

interface OrderDetailViewProps {
  order: OrderDetailData;
}

const statusSteps = ['CONFIRMED', 'PROCESSING', 'FULFILLED', 'COMPLETED'];
const statusLabels: Record<string, string> = {
  CONFIRMED: 'Confirmed',
  PROCESSING: 'Processing',
  FULFILLED: 'Shipped',
  COMPLETED: 'Delivered',
  CANCELLED: 'Cancelled',
};

export const OrderDetailView: React.FC<OrderDetailViewProps> = ({ order }) => {
  const pathname = usePathname();
  const locale = pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] ?? 'en';

  const formatPrice = (amount: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: order.currency,
    }).format(amount);

  const currentStep = statusSteps.indexOf(order.status);

  return (
    <div className="animate-[fadeIn_0.3s_ease-out] pb-12">
      {/* Back */}
      <Link
        href={`/${locale}/account/orders`}
        className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900 mb-6 transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Orders
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-semibold text-stone-900 tracking-tight">
            Order #{order.orderNumber}
          </h2>
          <p className="text-stone-500 mt-1">
            Placed on{' '}
            {new Date(order.createdAt).toLocaleDateString(locale, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-stone-100 text-stone-700 text-sm font-medium">
          {order.status === 'CANCELLED' ? (
            <Clock size={16} />
          ) : (
            <Package size={16} />
          )}
          {statusLabels[order.status] ?? order.status}
        </span>
      </div>

      {/* Progress */}
      {order.status !== 'CANCELLED' && (
        <div className="mb-10 p-6 rounded-xl border border-stone-200 bg-white">
          <div className="flex items-center justify-between">
            {statusSteps.map((step, i) => {
              const isActive = i <= currentStep;
              const Icon =
                i === 0 ? CheckCircle : i === 1 ? Clock : i === 2 ? Truck : Package;
              return (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={`p-2 rounded-full ${isActive ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-400'}`}
                    >
                      <Icon size={18} />
                    </div>
                    <span
                      className={`text-xs font-medium ${isActive ? 'text-stone-900' : 'text-stone-400'}`}
                    >
                      {statusLabels[step]}
                    </span>
                  </div>
                  {i < statusSteps.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 ${i < currentStep ? 'bg-stone-900' : 'bg-stone-200'}`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Items */}
        <div className="lg:col-span-2 space-y-3">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Items</h3>
          {order.lineItems.map((item) => (
            <div
              key={item.id}
              className="flex gap-4 p-4 rounded-xl border border-stone-200 bg-white"
            >
              <div className="w-16 h-16 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0">
                {item.imageUrl ? (
                  <Image
                    src={item.imageUrl}
                    alt={item.productTitle}
                    width={64}
                    height={64}
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-300">
                    <Package size={20} />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-stone-900">{item.productTitle}</p>
                {item.variantTitle && (
                  <p className="text-sm text-stone-500">{item.variantTitle}</p>
                )}
                <p className="text-sm text-stone-500 mt-1">
                  Qty: {item.quantity} &times; {formatPrice(item.unitPrice)}
                </p>
              </div>
              <span className="font-semibold text-stone-900">
                {formatPrice(item.unitPrice * item.quantity)}
              </span>
            </div>
          ))}
        </div>

        {/* Summary + Address */}
        <div className="space-y-6">
          <div className="rounded-xl border border-stone-200 bg-white p-5">
            <h3 className="text-lg font-semibold text-stone-900 mb-4">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-stone-600">
                <span>Subtotal</span>
                <span>{formatPrice(order.subtotal)}</span>
              </div>
              {order.discountTotal > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-{formatPrice(order.discountTotal)}</span>
                </div>
              )}
              <div className="flex justify-between text-stone-600">
                <span>Shipping</span>
                <span>{formatPrice(order.shippingTotal)}</span>
              </div>
              <div className="flex justify-between text-stone-600">
                <span>Tax</span>
                <span>{formatPrice(order.taxTotal)}</span>
              </div>
              <div className="flex justify-between text-lg font-semibold text-stone-900 pt-3 border-t border-stone-100">
                <span>Total</span>
                <span>{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>

          {order.shippingAddress && (
            <div className="rounded-xl border border-stone-200 bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={16} className="text-stone-400" />
                <h3 className="font-semibold text-stone-900">
                  Shipping Address
                </h3>
              </div>
              <div className="text-sm text-stone-600 space-y-0.5">
                <p>{order.shippingAddress.line1}</p>
                {order.shippingAddress.line2 && <p>{order.shippingAddress.line2}</p>}
                <p>
                  {order.shippingAddress.city}
                  {order.shippingAddress.state && `, ${order.shippingAddress.state}`}{' '}
                  {order.shippingAddress.postalCode}
                </p>
                <p>{order.shippingAddress.country}</p>
              </div>
            </div>
          )}

          {order.trackingNumber && (
            <div className="rounded-xl border border-stone-200 bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <Truck size={16} className="text-stone-400" />
                <h3 className="font-semibold text-stone-900">Tracking</h3>
              </div>
              <p className="text-sm text-stone-600">
                Tracking: {order.trackingNumber}
              </p>
              {order.trackingUrl && (
                <a
                  href={order.trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 mt-1 inline-block"
                >
                  Track Package &rarr;
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
